'use client';

import React, { createContext, useCallback, useContext, useSyncExternalStore } from 'react';

import { getBreadcrumbSnapshot, pushBreadcrumb, subscribeToBreadcrumbs } from './breadcrumbStore';
import type { Breadcrumb } from './ontology/types';

/** Marks that a BreadcrumbProvider is present so consuming hooks can fail fast when it is missing. */
const BreadcrumbContext = createContext<boolean | undefined>(undefined);

/** Exposes the manual breadcrumb append API used by descendants that need extra event coverage. */
const AddBreadcrumbContext = createContext<
  ((breadcrumb: Omit<Breadcrumb, 'ts'>) => void) | undefined
>(undefined);

/** Wires breadcrumb consumers to the shared breadcrumb store and exposes the optional manual append API. */
export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const addBreadcrumb = useCallback((breadcrumb: Omit<Breadcrumb, 'ts'>) => {
    pushBreadcrumb(breadcrumb);
  }, []);

  return (
    <AddBreadcrumbContext.Provider value={addBreadcrumb}>
      <BreadcrumbContext.Provider value={true}>{children}</BreadcrumbContext.Provider>
    </AddBreadcrumbContext.Provider>
  );
};

/** Returns the current breadcrumb trail, re-rendering when it changes; must be used within a BreadcrumbProvider. */
export const useBreadcrumbs = (): Breadcrumb[] => {
  const context = useContext(BreadcrumbContext);
  const breadcrumbs = useSyncExternalStore(
    subscribeToBreadcrumbs,
    getBreadcrumbSnapshot,
    getBreadcrumbSnapshot
  );

  if (context === undefined) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }

  return breadcrumbs;
};

/** Returns the manual breadcrumb append function and requires a surrounding BreadcrumbProvider. */
export const useAddBreadcrumb = (): ((breadcrumb: Omit<Breadcrumb, 'ts'>) => void) => {
  const context = useContext(AddBreadcrumbContext);
  if (context === undefined) {
    throw new Error('useAddBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
};
